import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import Modal from "../../../../parts/modal-new";
import QuestionDisplay from "../../../athlete-portal/profile/components/QuestionDisplay";
import submit from "submit-form";
import Select from "../../../../parts/inputs/select";
import SvgIcon from "../../../../parts/SvgIcon";

const QuestionnaireOnlyTemplates = ({
  userToLoadInformation,
  organizationOwner,
  templateId,
}) => {
  const { t } = useTranslation("coaching-portal");
  const templates =
    organizationOwner?.questionnaireTemplates?.templates ||
    userToLoadInformation?.questionnaireTemplates?.templates ||
    [];
  const defaultTemplate =
    organizationOwner?.questionnaireTemplates?.defaultTemplate ??
    userToLoadInformation?.questionnaireTemplates?.defaultTemplate;

  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [mobileOptions, setMobileOptions] = useState([]);

  useEffect(() => {
    if (!templates?.length) return;

    const defaultTemplateOb = templates.find(
      (template) => template._id === defaultTemplate
    );

    const options = templates.map((template) => ({
      value: template._id,
      label: template.title,
    }));

    const preselectedTemplate = templateId
      ? templates.find((template) => template._id === templateId)
      : null;

    const initialSelection =
      preselectedTemplate || defaultTemplateOb || templates[0];

    setMobileOptions(options);
    setSelectedTemplate(initialSelection);
  }, [templates, defaultTemplate, templateId]);

  const handleSetDefault = () => {
    const isDefault = selectedTemplate._id === defaultTemplate;
    const url = isDefault
      ? "/actions/questionnaire/remove-default-template"
      : "/actions/questionnaire/add-default-template";

    if (
      process.browser &&
      selectedTemplate._id === defaultTemplate &&
      !confirm(
        t("allTemplates.questionnaireOnlyTemplates.removeDefaultConfirm")
      )
    ) {
      return;
    }

    submit(url, {
      method: "POST",
      body: {
        templateId: selectedTemplate._id,
        userToRequest: organizationOwner?._id || userToLoadInformation._id,
        ...(organizationOwner && {
          organizationOwner: organizationOwner._id,
        }),
      },
    });
  };

  const removeTemplate = (id) => {
    if (confirm(t("checkInTemplates.confirmDeleteTemplate"))) {
      submit("/actions/questionnaire/remove-template", {
        method: "POST",
        body: {
          templateId: id,
          userToRequest: userToLoadInformation._id,
          ...(organizationOwner && {
            organizationOwner: organizationOwner._id,
          }),
        },
      });
    }
  };

  return (
    <Modal
      toClear={["section", "templateId"]}
      className={"checkin-template-modal"}
      title={t("allTemplates.questionnaireOnlyTemplates.title")}
    >
      <div className="body">
        {!templates.length ? (
          <section className="no-questions">
            <h4>
              {t(
                "allTemplates.questionnaireOnlyTemplates.noTemplatesAvailable"
              )}
            </h4>
            <p className="p5">
              {t("allTemplates.questionnaireOnlyTemplates.createFirstHint")}
            </p>
            <a
              role="button"
              data-button-type="primary"
              href="/actions/refresh?modal[type]=add-questionnaire-template"
            >
              {t("allTemplates.questionnaireOnlyTemplates.addNewTemplate")}
            </a>
          </section>
        ) : (
          <>
            <aside className="force-mobile-hidden">
              <p className="p5">
                {t("allTemplates.questionnaireOnlyTemplates.selectTemplate")}
              </p>
              <ul>
                {templates?.map((item, i) => {
                  return (
                    <li
                      key={`${item.title}-${i}-column`}
                      aria-current={
                        selectedTemplate?._id === item._id ? true : undefined
                      }
                      onClick={() => setSelectedTemplate(item)}
                    >
                      <p className="p1">{item.title}</p>
                      {selectedTemplate &&
                      selectedTemplate._id === item._id &&
                      selectedTemplate._id !== defaultTemplate ? (
                        <div
                          onClick={() => removeTemplate(item._id)}
                          className="questionnaire-delete-button"
                        >
                          <img
                            src="/img/new-icons/red-trash.svg"
                            alt="delete"
                          />
                        </div>
                      ) : null}
                    </li>
                  );
                })}
              </ul>
            </aside>
            <section className="mobile-templates-list only-show-mobile">
              <p className="p5">
                {t("allTemplates.questionnaireOnlyTemplates.selectTemplate")}
              </p>
              <div className="select">
                <Select
                  instanceId="questionnaire-only-templates-select"
                  autosize={false}
                  matchPos="start"
                  clearable={false}
                  options={mobileOptions}
                  simpleValue
                  onChange={(value) => {
                    const template = templates.find(
                      (templ) => templ._id === value
                    );
                    setSelectedTemplate(template);
                  }}
                  value={selectedTemplate?.title}
                />
              </div>
            </section>
            {selectedTemplate && (
              <section>
                <header>
                  <h5>{String(selectedTemplate.title)}</h5>
                  <div>
                    <button
                      type="button"
                      onClick={() => removeTemplate(selectedTemplate._id)}
                      data-button-type="text-white"
                      data-icon-only
                      sz="medium"
                    >
                      <img src="/img/new-icons/red-trash.svg" alt="delete" />
                    </button>
                    <a
                      className="force-mobile-hidden"
                      role="button"
                      data-button-type="tertiary"
                      href={`/actions/refresh?modal[type]=add-questionnaire-template&templateId=${selectedTemplate._id}&section=questionnaire`}
                    >
                      {t(
                        "allTemplates.questionnaireOnlyTemplates.editTemplate"
                      )}
                    </a>
                    <a
                      className="only-show-mobile"
                      href={`/actions/refresh?modal[type]=add-questionnaire-template&templateId=${selectedTemplate._id}&section=questionnaire`}
                      role="button"
                      data-button-type="tertiary"
                    >
                      <img src="/img/new-icons/edit-pencil.svg" alt="edit" />
                    </a>
                  </div>
                </header>
                <section id="questionnaire-modal-templates-body-questions">
                  {selectedTemplate.questions.map((question) => (
                    <QuestionDisplay
                      body={question.body}
                      title={question.title}
                      type={question.type}
                      id={question._id}
                      key={question._id}
                      templateDisplay={true}
                    />
                  ))}
                </section>
              </section>
            )}
          </>
        )}
      </div>
      {selectedTemplate && (
        <>
          {/* footer for desktop - no Save button */}
          <footer className="acc-template-footer force-mobile-hidden">
            <aside>
              <a
                href="/actions/refresh?modal[type]=add-questionnaire-template"
                role="button"
                data-button-type="outline"
              >
                {t("allTemplates.questionnaireOnlyTemplates.addNewTemplate")}
              </a>
              <div
                onClick={handleSetDefault}
                role="button"
                data-button-type="outline"
              >
                <SvgIcon
                  src={`/img/new-icons/${
                    selectedTemplate?._id === defaultTemplate ? "" : "un"
                  }checked-square.svg`}
                  noFilterOnLight={
                    selectedTemplate?._id === defaultTemplate ? true : false
                  }
                />
                <p className="p4">
                  {t("allTemplates.questionnaireOnlyTemplates.setAsDefault")}
                </p>
              </div>
            </aside>
          </footer>
          {/* footer for mobile - no Save button */}
          <footer className="acc-template-footer force-only-show-mobile">
            <div className="div-wrapper-footer-acc">
              <div onClick={handleSetDefault}>
                <SvgIcon
                  src={`/img/new-icons/${
                    selectedTemplate?._id === defaultTemplate ? "" : "un"
                  }checked-square.svg`}
                />
                <p className="p4">
                  {t("allTemplates.questionnaireOnlyTemplates.setAsDefault")}
                </p>
              </div>
              <div>
                <a
                  href="/actions/refresh?modal[type]=add-questionnaire-template"
                  role="button"
                  data-button-type="outline"
                >
                  {t("allTemplates.questionnaireOnlyTemplates.addNewTemplate")}
                </a>
              </div>
            </div>
          </footer>
        </>
      )}
    </Modal>
  );
};

export default QuestionnaireOnlyTemplates;
